import { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import tracking from '@g4/g4-analytics';
import moment from '@g4/moment-utc';
import { hook, withValue } from '@g4/automation-hooks';
import get from 'lodash/get';

import GAClickEventTracker from '../../../../../components/GAClickEventTracker.js';
import { useTranslations } from '../../../../../graphql/components/TranslationsProvider.js';
import { waterBlue } from '@g4/boneyard/lib/theme/colors';
import {
  useBookingPath,
  useCart,
  useSSRQuery,
} from '../../../../../graphql/index.js';
import {
  VerticalSpacing,
  Box,
  Text,
  Popover,
  Media,
  VisuallyHidden,
  Spinner,
  useMedia,
} from '../../../../../components/Boneyard.js';
import Price from '@g4/next-furniture/Price/Price';
import Markdown from '../../../../../components/Markdown.js';
import FlightOperator from '../../../../../components/FlightOperator.js';
import PopoverContent from './flightItem/PopoverContent.js';
import FLIGHT_ITEM_QUERY from './flightItem/flightItem.query.graphql.js';

import PLANE_ICON from '../../../../../images/plane-icon.svg';
import OVERNIGHT_ICON from '../../../../../images/overnight-icon.svg';
import {
  DEPARTING,
  RETURNING,
  WARNING_DEPARTING_AFTER_RETURNING,
} from '../../../constants.js';
import { sortOrder } from '../../../../../utils/constants/trackingConstants.js';
import { sortAncillariesByOrder, buildMobileBundleLabel } from '../../../../../utils/ancillaryUtils.js';
import { tripflexCode, ancillaryTypes } from '../../../../ancillariesPage/constants.js';

import {
  FLIGHT_NUMBER_LABEL,
  FLIGHT_NUMBER,
  FLIGHT_DEPARTURE_LABEL,
  FLIGHT_DEPARTURE_TIME,
  FLIGHT_ARRIVAL_LABEL,
  FLIGHT_ARRIVAL_TIME,
  SEATS_AVAILABILITY_TEXT,
  SEATS_AVAILABLE_TEXT,
  STRIKETHROUGH_FLIGHT_PRICE,
  FLIGHT_PRICE,
  LOYALTY_DISCOUNTED_FLIGHT_PRICE,
  SELECTED_FLIGHT,
  UNSELECTED_FLIGHT,
  PRICE_BOX,
  OVERNIGHT_FLIGHT_LABEL,
  OTP_POPOVER,
  DISABLED_FLIGHT,
  FLIGHTS_PAGE_BUNDLE_OPTION_OPEN,
} from '../../../../../../automationHooks.json';
import getFlightsRawProducts from '../../../getFlightsRawProducts.js';
import { getAffinityString } from '../../../../app/trackingItems.js';

import infoIconImage from '../../../../../images/info.svg';
import { getBundleRawProduct } from '../../../../bundlesPage/getBundleRawProducts.js';

const propTypes = {
  isFlightsQueryLoading: PropTypes.bool.isRequired,
  theme: PropTypes.object,
  flightOption: PropTypes.object.isRequired,
  setFlightSelected: PropTypes.func,
  selected: PropTypes.bool,
  leg: PropTypes.oneOf(['departing', 'returning']),
  disableFlightIfSameDayBasedOnTime: PropTypes.bool,
  warning: PropTypes.object,
  deactivateWarning: PropTypes.func,
  providerId: PropTypes.string,
  isRedemptionEnabledForNavitaire: PropTypes.bool,
  flightSearchCriteria: PropTypes.object,
  googleAnalyticsData: PropTypes.object,
  selectedBundles: PropTypes.object,
  onBundleSelect: PropTypes.func,
  bundleOffers: PropTypes.array,
};

const FlightBox = styled(Box)`
  display: flex;
`;

const SelectedBundleBox = styled(Box)`
  width: 100%;
  display: flex;
  background-color: #ffdd40;
  border-radius: 2px;
  align-items: center;
  padding: ${(props) =>
    props.isMobile ? props.theme.spacing(1) : props.theme.spacing(2)};
  margin-top: ${(props) => (props.selected ? props.theme.spacing(5) : 0)};
  justify-content: ${(props) =>
    props.isMobile ? 'space-evenly' : 'space-around'};
  padding-top: ${(props) => (props.selected && props.isMobile ? '-3' : 1)};
`;

const NoBundleSelectedBox = styled(Box)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  background-color: #fef5e9;
`;

const FlightWrapper = styled.div`
  cursor: pointer;
  border-top: 2px solid
    ${(props) => props.theme.colors.flights.flightItem.divider};

  &:last-of-type {
    border-bottom: 2px solid
      ${(props) => props.theme.colors.flights.flightItem.divider};
  }
`;

const FlightNumberBox = styled(Box)`
  flex-basis: 100px;
  flex-grow: 0;

  ${(props) => props.theme.query.md} {
    flex-basis: 130px;
  }
`;

const FlightNumberText = styled(Text)`
  text-decoration: underline;
`;

const LabelHeader = styled.p`
  font-weight: 600;
  font-size: ${(props) => props.theme.toRem(16)};

  ${(props) => props.theme.query.md} {
    font-size: ${(props) => props.theme.toRem(16)};
  }
`;
const FlightTime = styled.p`
  color: ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  font-size: ${(props) => props.theme.toRem(18)};
  font-weight: 600;

  > span {
    font-size: ${(props) => props.theme.toRem(12)};
    font-weight: normal;
    padding-left: ${(props) => props.theme.spacing(1)};
  }

  ${(props) => props.theme.query.md} {
    font-size: ${(props) => props.theme.toRem(28)};
    > span {
      font-size: ${(props) => props.theme.toRem(18)};
    }
  }
`;
const FlightDetailsBox = styled(Box)`
  flex-grow: 1;

  ${/* sc-selector */ Box} {
    flex-grow: 1;
  }
`;

const TimeBox = styled(Box)`
  flex-basis: 100%;
  align-self: center;

  ${(props) => props.theme.query.md} {
    flex-basis: 60%;
  }
`;

const SeatsTextBox = styled(Box)`
  align-self: center;
  flex-basis: 100%;
  justify-content: center;
  align-items: center;

  ${/* sc-selector */ Text} {
    max-width: 100%;
    margin: 0 auto;
  }

  ${(props) => props.theme.query.md} {
    flex-basis: 40%;

    ${/* sc-selector */ Text} {
      max-width: 220px;
    }
  }
`;
const MobileBundleSubTitle = styled(Box)`
  align-items: center;
  justify-content: center;
  font-size: ${(props) => props.theme.toRem(12)};
  color: ${(props) => props.theme.colors.flights.flightItem.textSecondary};
  padding-right: ${(props) => props.theme.spacing(3)};
`;

const DepartureBox = styled(Box)`
  flex-basis: 20%;
  align-self: flex-start;
`;

const ArrivalBox = styled(Box)`
  flex-basis: 60%;
  padding-left: ${(props) => props.theme.spacing(6)};
  background: url(${PLANE_ICON}) no-repeat left top;
  background-size: 20px;
  align-self: flex-start;

  ${(props) => props.theme.query.md} {
    padding-left: ${(props) => props.theme.spacing(14)};
    margin-left: ${(props) => props.theme.spacing(4)};
    background-position: left center;
    background-size: 40px;
  }

  ${(props) => props.theme.query.lg} {
    padding-left: ${(props) => props.theme.spacing(18)};
  }
`;
const OvernightText = styled(Text)`
  line-height: ${(props) => props.theme.toRem(10)};
  padding-left: ${(props) => props.theme.toRem(12)};
  margin-top: ${(props) => props.theme.toRem(2)};
  background: url(${OVERNIGHT_ICON}) no-repeat left center;
  background-size: 10px;

  ${(props) => props.theme.query.md} {
    background-size: 15px;
    line-height: ${(props) => props.theme.toRem(15)};
    padding-left: ${(props) => props.theme.toRem(18)};
    margin-top: ${(props) => props.theme.toRem(5)};
  }
`;

const SelectedFlightText = styled(Text)`
  font-weight: ${(props) => (props.isMobile ? 500 : 600)};
  color: ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  text-align: center;
  padding-top: ${(props) => props.theme.spacing(2)};
`;

/* stylelint-disable no-descending-specificity */
const SelectFlightBox = styled(Box)`
  flex-basis: ${({ theme }) => theme.flights.list.priceWidth.mobile};
  flex-shrink: 0;
  background-color: ${(props) =>
    props.theme.colors.flights.flightItem.lightBackground};
  ${(props) =>
    props.selected &&
    `
    background-image: ${props.theme.colors.flights.flightItem.selected};
  `}

  ${(props) => props.theme.query.md} {
    flex-basis: ${({ theme }) => theme.flights.list.priceWidth.midSized};
  }

  ${(props) => props.theme.query.lg} {
    flex-basis: ${({ theme }) => theme.flights.list.priceWidth.desktop};
    min-height: ${(props) =>
      props.selected ? '160px' : props.minHeight || '160px'};
  }
`;

const PriceBox = styled.div`
  width: 100%;
  height: ${(props) =>
    props.strikePrice && props.isMobile
      ? '83%'
      : props.isMobile && props.noThanksLinkClicked
        ? '76%'
        : !props.noThanksLinkClicked && props.selected
          ? '80%'
          : props.noThanksLinkClicked
            ? '85%'
            : '70%'};

  position: relative;
  color: ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  font-size: 1.125rem;
  font-weight: 500;
  text-align: center;
  flex-direction: column;

  > p {
    position: relative;
    font-size: ${(props) => props.theme.toRem(12)};
    padding-top: ${(props) => props.theme.spacing(2)};
    padding-bottom: ${(props) => props.theme.spacing(1)};
  }

  .prices {
    display: flex;
    align-items: center;
    justify-content: center;

    > div {
      &:last-of-type {
        margin-left: ${(props) => props.theme.spacing(1)};
      }

      del {
        /* stylelint-disable-next-line declaration-no-important */
        position: relative;
        display: inline-block;
        text-decoration-line: none; /* we must override the native strikethrough line and add our custom one */

        &:after {
          position: absolute;
          content: '';
          left: 0;
          top: 50%;
          right: 0;
          border-top: 2px solid;
          border-color: inherit;
          transform: rotate(-25deg);
        }
      }
    }
  }

  /* Mobile specific centering */
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .prices {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;

      > div {
        margin-left: 0;
        margin-bottom: 4px;
        text-align: center;

        &:last-of-type {
          margin-left: 0;
          margin-bottom: 0;
        }
      }
    }
  }

  ${(props) => props.theme.query.md} {
    > p {
      font-size: ${(props) => props.theme.toRem(16)};
    }
  }

  ${(props) => props.theme.query.lg} {
    display: flex;
    padding: ${({ theme: { spacing } }) => `${spacing(6)} 0 0`};

    > p {
      position: absolute;
      left: 0;
      top: ${(props) => props.theme.spacing(3)};
      width: 100%;
      padding: 0 ${(props) => props.theme.spacing(4)};
      font-weight: 600;
    }
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  top: auto;
  left: -10000px;
  height: 1px;
  width: 1px;
  overflow: hidden;

  &:focus + ${/* sc-selector */ FlightBox} {
    outline: ${(props) => props.theme.colors.flights.flightItem.focus} solid
      thin;
  }
`;

const BundleTitle = styled.div`
  font-weight: 500;
  margin-bottom: 16px;
  font-size: 20px;
  color: ${(props) => props.theme.colors.flights.flightItem.black};
  padding-top: 20px;
`;

const BundleOptions = styled.div`
  display: flex;
  gap: 16px;
  i & > *:hover {
    border: 2px solid #f89728;
    background: #fef5e9;
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const BundleOption = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 2px solid ${(props) => props.theme.colors.flights.flightItem.divider};
  border-radius: 20px;
  background: white;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  box-sizing: border-box;

  ${(props) =>
    props.isSelected &&
    `
    border: 2px solid #F89728;
    background: #FEF5E9;
  `}

  &:hover {
    border: 2px solid #F89728;
    background: #FEF5E9;
  }

  &:hover button[data-hook*='addButton'] {
    background-color: ${(props) =>
      props.isSelected ? '#F89728' : '#FEF5E9'};
  }
`;

const BundleHeader = styled.div`
  padding: 16px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  font-size: 14px;
`;

const BundleContent = styled.div`
  flex: 1;
  padding: 8px 12px 0px 12px;
`;

const BundleFeature = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 12px;
  line-height: 1.4;
`;

const InfoIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-left: 4px;
  color: #6b7280;
  flex-shrink: 0;

  &:before {
    content: ${(props) => props.imgSrc}
    font-size: 12px;
  }
`;

const FeatureText = styled.span`
  flex: 1;
`;

const NoThanksLink = styled.a`
  display: block;
  color: ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 10px 10px;
  background-color: transparent;
  border: 1px solid
    ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  border-radius: 10px;
  text-align: center;
  text-decoration: none;
`;

const TabBox = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  height: 35px;
  max-width: 90px;
  background-color: ${(props) => props.color};
  border-radius: 8px;
`;

const BundleImage = styled.img`
  max-width: 90%;
`;

const AncillaryImage = styled.img`
  max-width: 10%;
  margin-right: ${props => props.isBagIcon ? '13px' : '8px'};
  width: ${props => props.isBagIcon ? '18px' : 'auto'};
  height: ${props => props.isBagIcon ? '18px' : 'auto'};
`;

const MobileBundleCard = styled.div`
  border: 1px solid ${(props) => props.theme.colors.flights.flightItem.divider};
  border-radius: 20px;
  margin: 15px;
  background: white;

  ${(props) =>
    props.isSelected &&
    `
    border: 2px solid #F89728;
    background: #FEF5E9;
  `}
`;

const MobileBundleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
`;

const MobileBundleContent = styled.div`
  padding: 16px;
`;

const MobileBundleFeature = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const AddButton = styled.button`
  background-color: ${(props) => (props.isSelected ? '#F89728' : 'white')};
  border: ${(props) => (props.isSelected ? '' : '1px solid #01579B')};
  color: ${(props) => (props.isSelected ? 'white' : '#01579B')};
  border-radius: 13px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoThanksButton = styled.button`
  display: block;
  width: 93%;
  margin-top: 16px;
  margin-bottom: 15px;
  padding: 10px 20px;
  margin-left: auto;
  margin-right: auto;
  background-color: transparent;
  border: 1px solid
    ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  border-radius: 10px;
  color: ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;

  &:hover {
    background-color: ${(props) =>
      props.theme.colors.flights.flightItem.lightBlue};
    color: white;
  }
`;

const BundleTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-top: 20px;
`;

const ExpandCollapseLink = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.flights.flightItem.lightBlue};
  font-size: 14px;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`;

const RefundPill = styled.div`
  box-sizing: border-box;
  border: 1px solid #00579B;
  border-radius: 18px;
  padding: 5px 6px;
  width: fit-content;
  background-color: #F0F4FB;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;

  font-size: 10px;
  font-weight: 500;
  font-family: Montserrat;
  text-align: center;
  color: #005695;
  line-height: 12px;

  @media (max-width: 1200px) {
    white-space: normal;
  }
`;

const FlightItemWithBundles = withTheme(
  ({
    showBundlesInfo = true,
    setFlightBundleInfoOpen,
    isFlightsQueryLoading,
    flightOption,
    selected,
    leg,
    setFlightSelected,
    theme,
    disabled,
    disableFlightIfSameDayBasedOnTime,
    warning,
    deactivateWarning,
    loyaltyDiscountedPrice,
    providerId,
    isRedemptionEnabledForNavitaire,
    flightSearchCriteria,
    googleAnalyticsData,
    oppositeLegSelection,
    selectedBundles,
    onBundleSelect,
    bundleOffers,
    allAncillariesList,
  }) => {
    const flightEl = useRef(null);
    const { data } = useSSRQuery(FLIGHT_ITEM_QUERY);
    const seatsAvailabilityThreshold = get(
      data,
      'application.configurations.seatsAvailabilityThreshold'
    );

    const [isAllExpanded, setIsAllExpanded] = useState(false);
    const [expandedBundles, setExpandedBundles] = useState(new Set());
    const prevShowBundlesInfoRef = useRef(false);

    const translations = useTranslations();
    const { isStadium } = useBookingPath();
    const { cart } = useCart();
    const mq = useMedia();
    const isMobile = mq.lt('md');

    const {
      flightNumberLabel,
      departureTimeLabel,
      arrivalTimeLabel,
      limitedSeatsAvailability,
      availableSeats,
      selectedFlightText,
      isOvernight,
      selectFlightLabel,
      returnFlightBeforeArrival,
      loyaltyDiscountedPriceText,
      loyaltyDiscountedPricePointsText,
      loyaltyDiscountedPriceDefaultText,
    } = translations.flightItem;
    const { operatedByLabel } = translations.common;

    const {
      bundleAndSaveDepartingFlightLabelHTML,
      bundleAndSaveReturningFlightLabelHTML,
      collapseAllLabelHTML,
      expandAllLabelHTML,
      addedSelectedBundleLabelHTML,
      noBundleLabelHTML,
      bagsIncludeLabelHTML,
      personalItemLabelHTML,
      carryOnLabelHTML,
      checkedBagLabelHTML,
      totalBundleSeatOptionsLabelHTML,
      bonusBundleSeatOptionsLabelHTML,
      basicBundleSeatOptionsLabelHTML,
      priorityBoardingLabelHTML,
      tripFlexLabelHTML,
      enhancedTripFlexLabelHTML,
      tripFlexOFOPLabelHTML,
      tripFlexInfoDescription,
      tripFlexOFOPInfoDescriptionHTML,
      priorityBoardingInfoDescription,
      skipBundleButtonLabelHTML,
      personalItemResponsiveLabelHTML,
      carryOnResponsiveLabelHTML,
      checkedBagResponsiveLabelHTML,
      allSeatOptionsResponsiveLabelHTML,
      mainSeatOptionResponsiveLabelHTML,
      tripFlexResponsiveLabelHTML,
      priorityBoardingResponsiveLabelHTML,
      enhancedTripFlexResponsiveLabelHTML,
    } = translations.flightItemWithBundle;

    const { bundlePageTextAssociatedWithCurrentPrice } = translations.bundlesPage;

    const operatedBy = flightOption?.flight?.operatedBy;
    const validSelected = selected && !disabled;
    const flightRouteDirection =
      flightOption.flight.origin.code + flightOption.flight.destination.code;
    const affinityString = getAffinityString(cart?.order);

    // Filter bundleOffers based on current flight's bundleReferences
    const flightBundleReferences = flightOption?.bundleReferences || [];
    const filteredBundleOffers =
      bundleOffers && bundleOffers.length > 0
        ? bundleOffers
            .filter((bundle) =>
              flightBundleReferences.includes(bundle.bundleReference)
            )
            .sort(
              (departTier, returnTier) =>
                (departTier.tier || 0) - (returnTier.tier || 0)
            ) // Sort by tier ascending
        : [];

    // Helper function to get icon from allAncillariesList by type
    const getAncillaryIcon = (type) => {
      const ancillary = allAncillariesList?.find(item => item.type === type);
      return ancillary?.icon || null;
    };

    // Use filtered bundleOffers for this specific flight
    const bundleOptions =
      filteredBundleOffers.map((bundle) => {
        // Group bag-related ancillaries
        const bagTypes = ['ONE_PERSONAL_ITEM', 'CARRY_ON_BAG', 'CHECK_IN_BAG'];
        const bagAncillaries =
          bundle.ancillaries?.filter((ancillary) =>
            bagTypes.includes(ancillary.type)
          ) || [];
        const otherAncillaries =
          bundle.ancillaries?.filter(
            (ancillary) => !bagTypes.includes(ancillary.type)
          ) || [];

        // Sort otherAncillaries using utility function
        const ancillaryOrder = ['SEAT', 'PRIORITY_BOARDING', 'TRIP_FLEX'];
        const sortedOtherAncillaries = sortAncillariesByOrder(
          otherAncillaries,
          ancillaryOrder
        );

        const resposiveOrdering = [...bagAncillaries, ...sortedOtherAncillaries];

        let mobileBundleAncillaries = '';
        if(isMobile){
          const labelMap = {
            personalItemResponsiveLabelHTML,
            carryOnResponsiveLabelHTML,
            checkedBagResponsiveLabelHTML,
            mainSeatOptionResponsiveLabelHTML,
            allSeatOptionsResponsiveLabelHTML,
            tripFlexResponsiveLabelHTML,
            priorityBoardingResponsiveLabelHTML,
            enhancedTripFlexResponsiveLabelHTML,
          };
          mobileBundleAncillaries = buildMobileBundleLabel(resposiveOrdering, bundle.tier, labelMap);
        }

        const features = [];

        // Add grouped bag feature if any bag ancillaries exist
        if (bagAncillaries.length > 0) {
          const bagItems = [];
          const personalItem = bagAncillaries.find(
            (a) => a.type === 'ONE_PERSONAL_ITEM'
          );
          const carryOn = bagAncillaries.find((a) => a.type === 'CARRY_ON_BAG');
          const checkedBag = bagAncillaries.find(
            (a) => a.type === 'CHECK_IN_BAG'
          );

          if (personalItem)
            bagItems.push(<Markdown source={personalItemLabelHTML} />);
          if (carryOn) bagItems.push(<Markdown source={carryOnLabelHTML} />);
          if (checkedBag)
            bagItems.push(<Markdown source={checkedBagLabelHTML} />);

          // Concatenate bagItems with "," separator
          const bagItemsSubtitle =
            bagItems.length > 0
              ? bagItems.map((item, index) => (
                  <span key={index}>
                    {item}
                    {index < bagItems.length - 1 && ', '}
                  </span>
                ))
              : bagItems;

          features.push({
            text: bagsIncludeLabelHTML,
            subtitle: bagItemsSubtitle,
            included: true,
            icon: getAncillaryIcon('CARRY_ON_BAG'), // Using carry-on as representative bag icon
          });
        }

        // Add other ancillaries as separate features
        sortedOtherAncillaries.forEach((ancillary) => {
          const getAncillarySubtitle = (
            type,
            numberOfIncludedItems,
            bundleTier
          ) => {
            const subtitleMap = {
              SEAT: getSeatSubtitleByTier(bundleTier),
              TRIP_FLEX: ancillary.code === tripflexCode.ENHANCED_TRIP_FLEX ? enhancedTripFlexLabelHTML : tripFlexLabelHTML,
              PRIORITY_BOARDING: priorityBoardingLabelHTML,
            };

            return (
              subtitleMap[type] ||
              (numberOfIncludedItems ? `${numberOfIncludedItems} included` : '')
            );
          };

          const getSeatSubtitleByTier = (tier) => {
            const seatTierMap = {
              1: basicBundleSeatOptionsLabelHTML,
              2: bonusBundleSeatOptionsLabelHTML,
              3: totalBundleSeatOptionsLabelHTML,
            };

            return seatTierMap[tier] || 'Seat selection available';
          };

          // Check if ancillary type has only subtitle (no title)
          const hasOnlySubtitle = ['TRIP_FLEX', 'PRIORITY_BOARDING'].includes(
            ancillary.type
          );

          features.push({
            text: hasOnlySubtitle ? null : ancillary.name || 'Feature',
            subtitle: (
              <Markdown
                source={getAncillarySubtitle(
                  ancillary.type,
                  ancillary.numberOfIncludedItems,
                  bundle.tier
                )}
              />
            ),
            included: true,
            icon: getAncillaryIcon(ancillary.type),
            hasOnlySubtitle: hasOnlySubtitle,
            info: hasOnlySubtitle,
            ancillaryType: ancillary.type
          });

          // Setting the ancillary code property to each ancillary object in features array
          features.forEach(feature => {
            const matchedItem = sortedOtherAncillaries.find(item => item.type === feature.ancillaryType);
            feature.code = matchedItem ? matchedItem.code : null;
            // Setting icon for enhanced tripflex
            if(matchedItem?.type === ancillaryTypes.TRIP_FLEX && matchedItem?.code === tripflexCode.ENHANCED_TRIP_FLEX) {
              feature.icon = matchedItem ? matchedItem.icon : null;
            }
          })
        });

        return {
          type: bundle.name?.toLowerCase() || 'unknown',
          title: bundle.name.split(' ')[1] || 'Bundle',
          price: bundle.bundlePricePerPerson?.amount?.toString() || '0',
          tier: bundle.tier || 1,
          icon: bundle.icon || bundle.banner,
          features: features,
          mobileBundleAncillaries: mobileBundleAncillaries,
        };
      }) || [];

    let productList;
    if (googleAnalyticsData.data) {
      const googleAnalyticsFlightMarketData =
        googleAnalyticsData.data.application.googleAnalytics.flightMarkets;
      const selectedFlights = {
        departing: leg === 'departing' ? flightOption : oppositeLegSelection,
        returning: leg === 'returning' ? flightOption : oppositeLegSelection,
      };

      productList = getFlightsRawProducts(
        selectedFlights,
        flightSearchCriteria,
        loyaltyDiscountedPrice,
        affinityString,
        googleAnalyticsFlightMarketData,
        leg
      );
    }

    const handleBundleSelection = (bundleTier) => {
      onBundleSelect({
        leg: leg,
        tier: bundleTier,
      });

      setFlightSelected();
      setFlightBundleInfoOpen(null);

      const selectedBundle = bundleOffers.find(
        (bundle) => bundle.tier === bundleTier
      );
      const bundleRawProduct = getBundleRawProduct(
        selectedBundle,
        flightSearchCriteria,
        affinityString,
        flightOption.flight
      ).items;
      const rawProducts = [...productList, bundleRawProduct];
      tracking.trackSelectItem({
        name: sortOrder.DEFAULT,
        rawProducts: rawProducts,
      });
    };

    const handleNoBundle = () => {
      onBundleSelect({
        leg: leg,
        tier: null,
      });

      setFlightSelected();
      setFlightBundleInfoOpen(null);
      tracking.trackSelectItem({
        name: sortOrder.DEFAULT,
        rawProducts: productList,
      });
    };

    const currentSelectedTier =
      selectedBundles?.[leg === 'departing' ? 'outboundTier' : 'inboundTier'];
    const selectedBundleOption = currentSelectedTier
      ? bundleOptions.find((bundle) => bundle.tier === currentSelectedTier)
      : null;

    const noThanksLinkClicked = validSelected && currentSelectedTier === null;

    const seatsAvailability = (withHooks = true) => {
      if (
        flightOption.availableSeatsCount < seatsAvailabilityThreshold ||
        disableFlightIfSameDayBasedOnTime
      ) {
        return (
          <Media>
            {(mq) => (
              <Text
                size={mq.lt('md') ? 11 : 16}
                weight={500}
                automationHook={withHooks ? SEATS_AVAILABILITY_TEXT.hook : null}
                color={
                  disableFlightIfSameDayBasedOnTime
                    ? theme.colors.flights.flightItem.text
                    : theme.colors.flights.flightItem.alert
                }
              >
                <Markdown
                  source={
                    disableFlightIfSameDayBasedOnTime
                      ? returnFlightBeforeArrival
                      : limitedSeatsAvailability
                  }
                  data={
                    !disableFlightIfSameDayBasedOnTime
                      ? {
                          available_seats:
                            '<strong>' +
                            flightOption.availableSeatsCount +
                            '</strong>',
                          seat:
                            flightOption.availableSeatsCount > 1
                              ? 'seats'
                              : 'seat',
                        }
                      : {}
                  }
                />
              </Text>
            )}
          </Media>
        );
      }
      return (
        <Media>
          {(mq) => (
            <Text
              size={mq.lt('md') ? 11 : 16}
              automationHook={withHooks ? SEATS_AVAILABLE_TEXT.hook : null}
              weight={500}
            >
              {availableSeats}
            </Text>
          )}
        </Media>
      );
    };

    const renderloyaltyDiscount = (mq) => {
      if (loyaltyDiscountedPrice === null) {
        return (
          <Box
            h={45}
            pl={mq.lte('md') ? 0 : 8}
            pr={mq.lte('md') ? 0 : 8}
            automationHook={withValue(
              LOYALTY_DISCOUNTED_FLIGHT_PRICE.hook,
              'default-text'
            )}
          >
            <Text
              size={mq.lt('md') ? 10 : 12}
              color={theme.colors.common.textSecondary}
              lh={1.5}
            >
              {loyaltyDiscountedPriceDefaultText}
            </Text>
          </Box>
        );
      }

      if (loyaltyDiscountedPrice >= 0) {
        return (
          <Box
            h={45}
            automationHook={withValue(
              LOYALTY_DISCOUNTED_FLIGHT_PRICE.hook,
              'text'
            )}
          >
            <Text
              size={mq.lt('md') ? 10 : 12}
              color={theme.colors.common.textSecondary}
            >
              <Markdown
                source={loyaltyDiscountedPriceText}
                data={{
                  pointsValue: `$${Number(loyaltyDiscountedPrice).toFixed(2)}`,
                }}
              />
            </Text>
            <Text
              size={mq.lt('md') ? 10 : 12}
              color={theme.colors.common.textSecondary}
              lh={1.5}
            >
              {loyaltyDiscountedPricePointsText}
            </Text>
          </Box>
        );
      }

      return null;
    };

    const setFocus = () => {
      flightEl.current.focus();
    };

    const getPrice = (mq) => {
      return (
        <div>
          <Box
            d="flex"
            direction={mq.lt('lg') ? 'column' : 'row'}
            wrap={true}
            alignItems="center"
            justifyContent="center"
            pb={noThanksLinkClicked ? 3 : selectedBundleOption ? 2 : 0}
            pt={!noThanksLinkClicked && !selectedBundleOption ? 2 : 0}
          >
            {flightOption.strikethruPrice && (
              <Box>
                <del
                  aria-label={'original price: ' + flightOption.strikethruPrice}
                >
                  <Price
                    automationHook={STRIKETHROUGH_FLIGHT_PRICE.hook}
                    value={flightOption.strikethruPrice}
                    currency={'$'}
                    currencyFontSize={mq.lt('md') ? 8 : 13}
                    currencyFontWeight={600}
                    decimalFontSize={mq.lt('md') ? 8 : 12}
                    decimalFontWeight={500}
                    priceFontSize={mq.lt('md') ? 12 : 24}
                    priceFontWeight={500}
                    color={theme.colors.flights.flightItem.lightBlue}
                  />
                </del>
              </Box>
            )}
            <Box aria-label={'discounted price: ' + flightOption.price} pl={3}>
              <Price
                automationHook={FLIGHT_PRICE.hook}
                value={flightOption.price}
                currency={'$'}
                currencyFontSize={mq.lt('md') ? 12 : 23}
                currencyFontWeight={600}
                decimalFontSize={mq.lt('md') ? 12 : 20}
                decimalFontWeight={500}
                priceFontSize={mq.lt('md') ? 20 : 50}
                priceFontWeight={600}
                color={theme.colors.flights.flightItem.lightBlue}
              />
            </Box>
            {/**
             * display loyalty discount banner only for g4 flights
             * this condition will be removed in future
             */}
            {(providerId === 'g4' && renderloyaltyDiscount(mq)) ||
              (providerId === 'other' &&
                isRedemptionEnabledForNavitaire &&
                renderloyaltyDiscount(mq))}
          </Box>
        </div>
      );
    };

    const toggleBundleExpansion = (bundleType) => {
      setExpandedBundles((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(bundleType)) {
          newSet.delete(bundleType);
        } else {
          newSet.add(bundleType);
        }
        return newSet;
      });
    };

    const handleExpandCollapseAll = () => {
      if (isAllExpanded) {
        // Collapse all
        setExpandedBundles(new Set());
        setIsAllExpanded(false);
      } else {
        // Expand all
        const allBundleTypes = new Set(
          bundleOptions.map((bundle) => bundle.type)
        );
        setExpandedBundles(allBundleTypes);
        setIsAllExpanded(true);
      }
    };

    // When the bundle panel opens (showBundlesInfo transitions false to true), reset the
    // individual expansion state so the default-open behaviour is consistent whether the
    // data is fresh or returned from cache (revisited dates).
    useEffect(() => {
      if (showBundlesInfo && !prevShowBundlesInfoRef.current) {
        setExpandedBundles(new Set());
        setIsAllExpanded(false);
      }
      prevShowBundlesInfoRef.current = showBundlesInfo;
    }, [showBundlesInfo]);

    useEffect(() => {
      const allBundleTypes = bundleOptions.map((bundle) => bundle.type);
      const allExpanded =
        allBundleTypes.length > 0 &&
        allBundleTypes.every((type) => expandedBundles.has(type));
      setIsAllExpanded(allExpanded);
    }, [expandedBundles, bundleOptions]);

    const renderBundleAddButton = (bundle, isSelected, hookSuffix) => (
      <AddButton
        isSelected={isSelected}
        data-hook={`flight-${flightOption.id}-bundleType-${bundle.type}-${hookSuffix}`}
        onClick={(e) => {
          e.stopPropagation();
          if (isSelected) {
            handleNoBundle();
          } else {
            setFlightSelected();
            setFlightBundleInfoOpen(null);
            handleBundleSelection(bundle.tier);
          }
        }}
      >
        {isSelected ? '✓' : 'ADD'}
      </AddButton>
    );

    return (
      <Media>
        {(mq) => (
          <FlightWrapper
            {...(validSelected
              ? hook(SELECTED_FLIGHT.hook)
              : hook(
                  UNSELECTED_FLIGHT.hook,
                  `${leg}_${flightOption.flight.number}`
                ))}
            onClick={(e) => {
              if (!disabled) {
                if (
                  leg === DEPARTING &&
                  warning?.displayOn === RETURNING &&
                  warning?.key === WARNING_DEPARTING_AFTER_RETURNING
                ) {
                  deactivateWarning();
                }
                setFlightBundleInfoOpen(flightOption.id);
                setFocus();
              }
              e.stopPropagation();
            }}
          >
            <VisuallyHidden>
              <label htmlFor={flightOption.id + '-radio'}>
                {selectFlightLabel} {flightOption.flight.number}
                {departureTimeLabel}{' '}
                {moment(flightOption.flight.departingTime).format('h:mm')}{' '}
                {moment(flightOption.flight.departingTime).format('a')}
                {arrivalTimeLabel}{' '}
                {moment(flightOption.flight.arrivalTime).format('h:mm')}{' '}
                {moment(flightOption.flight.arrivalTime).format('a')}
                {flightOption.flight.isOvernight && isOvernight}
                {seatsAvailability(false)}
                original price: {flightOption.strikethruPrice}
                discounted price: {flightOption.price}
              </label>
            </VisuallyHidden>
            <HiddenInput
              ref={flightEl}
              onChange={setFlightSelected}
              type="radio"
              name="radio-choice"
              id={flightOption.id + '-radio'}
              value={flightOption.id}
              checked={validSelected}
              disabled={disabled || disableFlightIfSameDayBasedOnTime}
            />
            <FlightBox
              automationHook={
                disabled
                  ? DISABLED_FLIGHT.hook +
                    '_' +
                    `${leg}_${flightOption.flight.number}`
                  : null
              }
              backgroundColor={
                disabled || disableFlightIfSameDayBasedOnTime
                  ? theme.colors.flights.flightItem.lightBackground
                  : '#fff'
              }
            >
              {!mq.lte('md') && (
                <FlightNumberBox
                  p={mq.lt('md') ? 4 : 6}
                  backgroundColor={
                    theme.colors.flights.flightItem.lightBackground
                  }
                >
                  <Box>
                    <LabelHeader {...hook(FLIGHT_NUMBER_LABEL.hook)}>
                      {flightNumberLabel}
                    </LabelHeader>
                  </Box>
                  <VerticalSpacing size={2} />
                  <Box>
                    <Popover
                      content={<PopoverContent flightOption={flightOption} />}
                      label={'popover'}
                      arrowSize={mq.lte('sm') ? 0 : 50}
                      arrowOffset="-50px"
                      backgroundColor={theme.colors.popover.background}
                      maxWidth="530px"
                      showClose={mq.lte('sm')}
                      automationHook={
                        validSelected
                          ? withValue(OTP_POPOVER.hook, `selected_${leg}`)
                          : undefined
                      }
                    >
                      <GAClickEventTracker
                        onClick={() =>
                          tracking.trackEvent(
                            'site_click_tracking',
                            'Flight Chooser',
                            `OTP | ${flightRouteDirection} Flight ${flightOption.flight.number}`
                          )
                        }
                      >
                        <FlightNumberText
                          size={mq.lt('md') ? 16 : 22}
                          color={theme.colors.flights.flightItem.lightBlue}
                          weight={600}
                          automationHook={FLIGHT_NUMBER.hook}
                        >
                          {flightOption.flight.number}
                        </FlightNumberText>
                      </GAClickEventTracker>
                    </Popover>
                  </Box>
                </FlightNumberBox>
              )}
              <FlightDetailsBox
                d="flex"
                direction={mq.lte('md') ? 'column' : 'row'}
                pl={mq.lte('md') ? 0 : 8}
                mr={mq.lte('md') ? 0 : 2}
                mb={mq.lte('md') ? -3 : 0}
              >
                <Box flexGrow={1} mb={mq.lte('md') ? 3 : 0}>
                  {mq.lte('md') ? (
                    <>
                      {/* {mobile view code} */}
                      <Box>
                        <Box flexGrow={1} d="flex">
                          <FlightNumberBox
                            p={mq.lt('md') ? 4 : 13}
                            backgroundColor={
                              theme.colors.flights.flightItem.lightBackground
                            }
                          >
                            <Box>
                              <LabelHeader {...hook(FLIGHT_NUMBER_LABEL.hook)}>
                                {flightNumberLabel}
                              </LabelHeader>
                            </Box>
                            <Box>
                              <Popover
                                content={
                                  <PopoverContent flightOption={flightOption} />
                                }
                                label={'popover'}
                                arrowSize={mq.lte('sm') ? 0 : 50}
                                arrowOffset="-50px"
                                backgroundColor={
                                  theme.colors.popover.background
                                }
                                maxWidth="530px"
                                showClose={mq.lte('sm')}
                                automationHook={
                                  validSelected
                                    ? withValue(
                                        OTP_POPOVER.hook,
                                        `selected_${leg}`
                                      )
                                    : undefined
                                }
                              >
                                <GAClickEventTracker
                                  onClick={() =>
                                    tracking.trackEvent(
                                      'site_click_tracking',
                                      'Flight Chooser',
                                      `OTP | ${flightRouteDirection} Flight ${flightOption.flight.number}`
                                    )
                                  }
                                >
                                  <FlightNumberText
                                    size={mq.lt('md') ? 16 : 22}
                                    color={
                                      theme.colors.flights.flightItem.lightBlue
                                    }
                                    weight={600}
                                    automationHook={FLIGHT_NUMBER.hook}
                                  >
                                    {flightOption.flight.number}
                                  </FlightNumberText>
                                </GAClickEventTracker>
                              </Popover>
                            </Box>
                          </FlightNumberBox>
                          <TimeBox
                            d={mq.lte('md') ? 'column' : 'row'}
                            pl={mq.lte('md') ? 2 : 10}
                            pt={mq.lte('md') ? 1 : 5}
                          >
                            <Box d="flex" flexGrow={1}>
                              <DepartureBox>
                                <LabelHeader
                                  {...hook(FLIGHT_DEPARTURE_LABEL.hook)}
                                >
                                  {departureTimeLabel}
                                </LabelHeader>
                                <VerticalSpacing size={2} />

                                <FlightTime
                                  {...hook(FLIGHT_DEPARTURE_TIME.hook)}
                                >
                                  {moment(
                                    flightOption.flight.departingTime
                                  ).format('h:mm')}
                                  <span>
                                    {moment(flightOption.flight.departingTime)
                                      .format('a')
                                      .toUpperCase()}
                                  </span>
                                </FlightTime>
                                {!mq.lte('md') && operatedBy && (
                                  <FlightOperator
                                    operatedBy={operatedBy}
                                    operatedByLabel={operatedByLabel}
                                    display="flex"
                                    weight={500}
                                  ></FlightOperator>
                                )}
                              </DepartureBox>
                              <ArrivalBox>
                                <LabelHeader
                                  {...hook(FLIGHT_ARRIVAL_LABEL.hook)}
                                >
                                  {arrivalTimeLabel}
                                </LabelHeader>
                                <VerticalSpacing size={2} />
                                <FlightTime {...hook(FLIGHT_ARRIVAL_TIME.hook)}>
                                  {moment(
                                    flightOption.flight.arrivalTime
                                  ).format('h:mm')}
                                  <span>
                                    {moment(flightOption.flight.arrivalTime)
                                      .format('a')
                                      .toUpperCase()}
                                  </span>
                                </FlightTime>
                                {flightOption.flight.isOvernight && (
                                  <OvernightText
                                    automationHook={OVERNIGHT_FLIGHT_LABEL.hook}
                                    size={mq.lte('md') ? 10 : 12}
                                    color={
                                      theme.colors.flights.flightItem.lightBlue
                                    }
                                  >
                                    {isOvernight}
                                  </OvernightText>
                                )}
                              </ArrivalBox>
                            </Box>
                            <Box>
                              {mq.lte('md') && (
                                <SeatsTextBox
                                  d="flex"
                                  justifyContent="center"
                                  alignItems="center"
                                  pr={mq.lte('md') ? 0 : 14}
                                  pt={mq.lte('md') ? (operatedBy ? 2 : 4) : 0}
                                >
                                  {seatsAvailability()}
                                </SeatsTextBox>
                              )}
                            </Box>
                          </TimeBox>

                          <SelectFlightBox selected={validSelected}>
                            {!isFlightsQueryLoading && validSelected && (
                              <SelectedFlightText
                                size={mq.lt('md') ? 11 : 16}
                                isMobile={mq.lte('md')}
                              >
                                {selectedFlightText}
                              </SelectedFlightText>
                            )}
                            <PriceBox
                              {...hook(PRICE_BOX.hook)}
                              noThanksLinkClicked={noThanksLinkClicked}
                              isMobile={mq.lte('md')}
                              selected={validSelected}
                              strikePrice={flightOption.strikethruPrice}
                            >
                              <Box
                                className="prices"
                                d="flex"
                                direction={mq.lt('lg') ? 'column' : 'row'}
                              >
                                {isFlightsQueryLoading ? (
                                  <Spinner
                                    size="small"
                                    appearance="dark"
                                    automationHook={withValue(
                                      'price-box',
                                      'spinner'
                                    )}
                                  />
                                ) : !isStadium ? (
                                  <Box
                                    weight={mq.lte('md') ? 700 : 600}
                                    pt={mq.lt('md') ? 2 : 0}
                                  >
                                    {getPrice(mq)}
                                  </Box>
                                ) : (
                                  <p
                                    style={
                                      mq.lt('md')
                                        ? { paddingTop: '40px' }
                                        : { paddingTop: '12px' }
                                    }
                                  >
                                    <Text
                                      weight={600}
                                      wrap={true}
                                      size={mq.lt('md') ? 20 : 50}
                                    >
                                      <span style={{ fontSize: '15px' }}>
                                        {'Included'}
                                      </span>
                                    </Text>
                                  </p>
                                )}
                              </Box>
                              {validSelected &&
                              !noThanksLinkClicked &&
                              selectedBundleOption ? (
                                <SelectedBundleBox
                                  selected={selectedBundleOption}
                                  automationHook={`flight-${flightOption.id}-selectedBundle-${selectedBundleOption?.type}`}
                                  isMobile={mq.lte('md')}
                                >
                                  <svg
                                    width={mq.lt('md') ? '20' : '24'}
                                    height={mq.lt('md') ? '20' : '24'}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={
                                      mq.lt('md') ? {} : { marginRight: 8 }
                                    }
                                  >
                                    <circle
                                      cx="12"
                                      cy="12"
                                      r="12"
                                      fill="#058027"
                                    />
                                    <path
                                      d="M7 13.5L10.5 17L17 10"
                                      stroke="white"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  {!mq.lte('md') && (
                                    <Text
                                      weight={500}
                                      size={mq.lt('md') ? 10 : 16}
                                      color="#000000"
                                      style={
                                        mq.lt('md') ? {} : { marginRight: 8 }
                                      }
                                    >
                                      <Markdown
                                        source={addedSelectedBundleLabelHTML}
                                        data={{
                                          selectedBundleName:
                                            selectedBundleOption.title,
                                        }}
                                      />
                                    </Text>
                                  )}
                                  <Text
                                    weight={mq.lte('md') ? 600 : 500}
                                    color={
                                      theme.colors.flights.flightItem.lightBlue
                                    }
                                    size={mq.lte('md') ? 14 : 16}
                                  >
                                    + ${selectedBundleOption.price}
                                  </Text>
                                </SelectedBundleBox>
                              ) : (
                                validSelected &&
                                noThanksLinkClicked && (
                                  <NoBundleSelectedBox
                                    automationHook={`flight-${flightOption.id}-noBundleSelected`}
                                  >
                                    <svg
                                      width="25"
                                      height="25"
                                      viewBox="0 0 28 28"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                      style={{ marginRight: 8 }}
                                    >
                                      <circle
                                        cx="14"
                                        cy="14"
                                        r={mq.lte('md') ? '9' : '13'}
                                        stroke="#222B2E"
                                        strokeWidth="2"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        style={{ marginRight: 8 }}
                                      />
                                      <text
                                        x={mq.lte('md') ? '14' : '14'}
                                        y={mq.lte('md') ? '20' : '19'}
                                        textAnchor="middle"
                                        fontSize="18"
                                        fontWeight="bold"
                                        fill="#222B2E"
                                        fontFamily="Arial, sans-serif"
                                      >
                                        !
                                      </text>
                                    </svg>
                                    <Text
                                      size={mq.lt('md') ? 12 : 16}
                                      color="#222B2E"
                                      weight={500}
                                      style={{
                                        fontFamily:
                                          'Montserrat, Arial, sans-serif',
                                        letterSpacing: '0.5px',
                                      }}
                                    >
                                      <Markdown source={noBundleLabelHTML} />
                                    </Text>
                                  </NoBundleSelectedBox>
                                )
                              )}
                            </PriceBox>
                          </SelectFlightBox>
                        </Box>
                        {mq.lte('md') && operatedBy && (
                          <FlightOperator
                            operatedBy={operatedBy}
                            operatedByLabel={operatedByLabel}
                            display="flex"
                            weight={500}
                          ></FlightOperator>
                        )}
                      </Box>
                    </>
                  ) : (
                    <Box>
                      <Box flexGrow={1} d="flex" justifyContent="space-between">
                        <TimeBox d="flex">
                          <Box d="flex" flexGrow={1}>
                            <Box d="flex" flexGrow={1}>
                              <DepartureBox>
                                <LabelHeader
                                  {...hook(FLIGHT_DEPARTURE_LABEL.hook)}
                                >
                                  {departureTimeLabel}
                                </LabelHeader>
                                <VerticalSpacing size={2} />

                                <FlightTime
                                  {...hook(FLIGHT_DEPARTURE_TIME.hook)}
                                >
                                  {moment(
                                    flightOption.flight.departingTime
                                  ).format('h:mm')}
                                  <span>
                                    {moment(flightOption.flight.departingTime)
                                      .format('a')
                                      .toUpperCase()}
                                  </span>
                                </FlightTime>
                                {!mq.lte('md') && operatedBy && (
                                  <FlightOperator
                                    operatedBy={operatedBy}
                                    operatedByLabel={operatedByLabel}
                                    display="flex"
                                    weight={500}
                                  ></FlightOperator>
                                )}
                              </DepartureBox>
                              <ArrivalBox>
                                <LabelHeader
                                  {...hook(FLIGHT_ARRIVAL_LABEL.hook)}
                                >
                                  {arrivalTimeLabel}
                                </LabelHeader>
                                <VerticalSpacing size={2} />
                                <FlightTime {...hook(FLIGHT_ARRIVAL_TIME.hook)}>
                                  {moment(
                                    flightOption.flight.arrivalTime
                                  ).format('h:mm')}
                                  <span>
                                    {moment(flightOption.flight.arrivalTime)
                                      .format('a')
                                      .toUpperCase()}
                                  </span>
                                </FlightTime>
                                {flightOption.flight.isOvernight && (
                                  <OvernightText
                                    automationHook={OVERNIGHT_FLIGHT_LABEL.hook}
                                    size={mq.lte('md') ? 10 : 12}
                                    color={
                                      theme.colors.flights.flightItem.lightBlue
                                    }
                                  >
                                    {isOvernight}
                                  </OvernightText>
                                )}
                              </ArrivalBox>
                            </Box>
                            <SeatsTextBox
                              d="flex"
                              justifyContent="center"
                              alignItems="center"
                              pr={mq.lte('md') ? 0 : 14}
                              pt={mq.lte('md') ? (operatedBy ? 2 : 4) : 0}
                            >
                              {seatsAvailability()}
                            </SeatsTextBox>
                          </Box>
                        </TimeBox>

                        <SelectFlightBox selected={validSelected}>
                          {!isFlightsQueryLoading && validSelected && (
                            <SelectedFlightText>
                              {selectedFlightText}
                            </SelectedFlightText>
                          )}
                          <PriceBox
                            {...hook(PRICE_BOX.hook)}
                            noThanksLinkClicked={noThanksLinkClicked}
                          >
                            <Box
                              className="prices"
                              d="flex"
                              direction={mq.lt('lg') ? 'column' : 'row'}
                            >
                              {isFlightsQueryLoading ? (
                                <Spinner
                                  size="small"
                                  appearance="dark"
                                  automationHook={withValue(
                                    'price-box',
                                    'spinner'
                                  )}
                                />
                              ) : !isStadium ? (
                                getPrice(mq)
                              ) : (
                                <p
                                  style={
                                    mq.lt('md')
                                      ? { paddingTop: '60px' }
                                      : { paddingTop: '12px' }
                                  }
                                >
                                  <Text
                                    weight={600}
                                    wrap={true}
                                    size={mq.lt('md') ? 20 : 50}
                                  >
                                    <span style={{ fontSize: '15px' }}>
                                      {'Included'}
                                    </span>
                                  </Text>
                                </p>
                              )}
                            </Box>
                            {validSelected &&
                            !noThanksLinkClicked &&
                            selectedBundleOption ? (
                              <SelectedBundleBox
                                selected={selectedBundleOption}
                                automationHook={`flight-${flightOption.id}-selectedBundle-${selectedBundleOption?.type}`}
                              >
                                <svg
                                  width={mq.lt('md') ? '20' : '24'}
                                  height={mq.lt('md') ? '20' : '24'}
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  style={mq.lt('md') ? {} : { marginRight: 8 }}
                                >
                                  <circle
                                    cx="12"
                                    cy="12"
                                    r="12"
                                    fill="#058027"
                                  />
                                  <path
                                    d="M7 13.5L10.5 17L17 10"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                <Text
                                  weight={500}
                                  size={mq.lt('md') ? 10 : 16}
                                  color="#000000"
                                  style={mq.lt('md') ? {} : { marginRight: 8 }}
                                >
                                  <Markdown
                                    source={addedSelectedBundleLabelHTML}
                                    data={{
                                      selectedBundleName:
                                        selectedBundleOption.title,
                                    }}
                                  />
                                </Text>
                                <Text
                                  weight={500}
                                  color={
                                    theme.colors.flights.flightItem.lightBlue
                                  }
                                  size={mq.lt('md') ? 10 : 16}
                                >
                                  + ${selectedBundleOption.price}
                                </Text>
                              </SelectedBundleBox>
                            ) : (
                              validSelected &&
                              noThanksLinkClicked && (
                                <NoBundleSelectedBox
                                  automationHook={`flight-${flightOption.id}-noBundleSelected`}
                                >
                                  <svg
                                    width="25"
                                    height="25"
                                    viewBox="0 0 28 28"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{ marginRight: 8 }}
                                  >
                                    <circle
                                      cx="14"
                                      cy="14"
                                      r="13"
                                      stroke="#222B2E"
                                      strokeWidth="2"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                      style={{ marginRight: 8 }}
                                    />
                                    <text
                                      x="14"
                                      y="19"
                                      textAnchor="middle"
                                      fontSize="18"
                                      fontWeight="bold"
                                      fill="#222B2E"
                                      fontFamily="Arial, sans-serif"
                                    >
                                      !
                                    </text>
                                  </svg>
                                  <Text
                                    size={mq.lt('md') ? 12 : 16}
                                    color="#222B2E"
                                    weight={500}
                                    style={{
                                      fontFamily:
                                        'Montserrat, Arial, sans-serif',
                                      letterSpacing: '0.5px',
                                    }}
                                  >
                                    <Markdown source={noBundleLabelHTML} />
                                  </Text>
                                </NoBundleSelectedBox>
                              )
                            )}
                          </PriceBox>
                        </SelectFlightBox>
                      </Box>
                      {mq.lte('md') && operatedBy && (
                        <FlightOperator
                          operatedBy={operatedBy}
                          operatedByLabel={operatedByLabel}
                          display="flex"
                          weight={500}
                        ></FlightOperator>
                      )}
                    </Box>
                  )}
                  {showBundlesInfo && (
                    <Box
                      mb={6}
                      automationHook={withValue(
                        FLIGHTS_PAGE_BUNDLE_OPTION_OPEN.hook,
                        `flight-${flightOption.id}`
                      )}
                    >
                      <Media>
                        {(mq) => (
                          <>
                            {mq.lte('md') ? (
                              <BundleTitleRow style={{ flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                <BundleTitle
                                  data-hook={`flight-${flightOption.id}-BundleAndSaveTitle`}
                                  style={{ margin: 0, paddingTop: 0, textAlign: 'center', width: '100%' ,fontSize: '18px'}}
                                >
                                  <Markdown source={leg === DEPARTING ? bundleAndSaveDepartingFlightLabelHTML : bundleAndSaveReturningFlightLabelHTML} />
                                </BundleTitle>
                                <ExpandCollapseLink
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleExpandCollapseAll();
                                  }}
                                  data-hook={`flight-${flightOption.id}-expandCollapseLink`}
                                >
                                  {isAllExpanded
                                    ? <Markdown source={collapseAllLabelHTML} />
                                    : <Markdown source={expandAllLabelHTML} />}
                                </ExpandCollapseLink>
                              </BundleTitleRow>
                            ) : (
                              // Desktop: Show title with No Thanks link
                              <BundleTitleRow>
                                <BundleTitle
                                  data-hook={`flight-${flightOption.id}-BundleAndSaveTitle`}
                                  style={{ marginBottom: 0, paddingTop: 0 }}
                                >
                                  <Markdown source={leg === DEPARTING ? bundleAndSaveDepartingFlightLabelHTML : bundleAndSaveReturningFlightLabelHTML} />
                                </BundleTitle>
                                <NoThanksLink
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    tracking.trackEvent({
                                      event: 'decline_flight_bundle',
                                      route: flightRouteDirection,
                                    });
                                    setFlightSelected();
                                    setFlightBundleInfoOpen(null);
                                    handleNoBundle();
                                  }}
                                  data-hook={`flight-${flightOption.id}-noThanksLink`}
                                >
                                  <Markdown source={skipBundleButtonLabelHTML} />
                                </NoThanksLink>
                              </BundleTitleRow>
                            )}
                          </>
                        )}
                      </Media>

                      <Media>
                        {(mq) =>
                          mq.lte('md') ? (
                            <div>
                              {bundleOptions.map((bundle) => {
                                const isSelected =
                                  currentSelectedTier === bundle.tier &&
                                  validSelected;
                                const isBundleExpanded = expandedBundles.has(
                                  bundle.type
                                );

                                return (
                                  <MobileBundleCard
                                    key={bundle.type}
                                    isSelected={isSelected}
                                    data-hook={`flight-${flightOption.id}-bundleType-${bundle.type}-selected-${isSelected}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleBundleExpansion(bundle.type);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <MobileBundleHeader>
                                      <div
                                        style={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          width: '100%',
                                        }}
                                      >
                                        {/* First row: Bundle image, price, and ADD button */}
                                        <div
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            width: '100%',
                                            marginBottom: '8px',
                                          }}
                                        >
                                          <TabBox
                                            color={get(theme.colors.bundle, [
                                              `tier_${bundle.tier}`,
                                              'label',
                                            ])}
                                            style={{
                                              marginRight: '12px',
                                              width: '80px',
                                              height: '35px',
                                              minWidth: '70px',
                                            }}
                                          >
                                            <BundleImage
                                              src={bundle.icon}
                                              alt="Bundle icon"
                                              style={{
                                                maxWidth: '90%',
                                              }}
                                            />
                                          </TabBox>

                                          <div
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              marginLeft: 'auto',
                                            }}
                                          >
                                            <div
                                              style={{
                                                paddingRight: '12px',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                              }}
                                            >
                                              <p
                                                style={{
                                                  margin: 0,
                                                  padding: 0,
                                                  fontSize: '24px',
                                                  fontWeight: '500',
                                                }}
                                              >
                                                +${bundle.price}*
                                              </p>
                                              <div style={{ fontSize: '12px', fontWeight: '400' }}>
                                                <Markdown source={bundlePageTextAssociatedWithCurrentPrice} />
                                              </div>
                                            </div>
                                            {renderBundleAddButton(bundle, isSelected, 'addButton')}
                                          </div>
                                        </div>

                                        {/* Second row: Bundle subtitle (only when collapsed) */}
                                        {!isBundleExpanded && (
                                          <div
                                            style={{
                                              display: 'flex',
                                              justifyContent: 'center',
                                              width: '100%',
                                              textAlign: 'start',
                                              flexDirection: 'column',
                                              gap: '4px'
                                            }}
                                          >
                                            <MobileBundleSubTitle>
                                              <Markdown source={bundle.mobileBundleAncillaries} />
                                            </MobileBundleSubTitle>
                                            {bundle.tier === 3 && bundle.features.some(feature => feature.code === tripflexCode.ENHANCED_TRIP_FLEX) && (
                                              <RefundPill>
                                                <Markdown source={tripFlexOFOPLabelHTML} />
                                              </RefundPill>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </MobileBundleHeader>
                                    {/* Show content based on individual bundle expansion OR global expansion */}
                                    {(isBundleExpanded || isAllExpanded) && (
                                      <MobileBundleContent
                                        data-hook={`flight-${flightOption.id}-bundleType-${bundle.type}-content-expanded`}
                                      >
                                        {bundle.features.map(
                                          (feature, index) => (
                                            <MobileBundleFeature key={index}>
                                              <AncillaryImage
                                                src={feature.icon}
                                                style={{
                                                  width: '16px',
                                                  height: '16px',
                                                  marginRight: '8px',
                                                  flexShrink: 0,
                                                }}
                                              />
                                              <div
                                                style={{
                                                  flex: 1,
                                                  fontSize: '12px',
                                                }}
                                              >
                                                {feature.text &&
                                                  !feature.hasOnlySubtitle && (
                                                    <div
                                                      style={{
                                                        fontSize: '12px',
                                                        color: '#6B7280',
                                                        marginBottom: '2px',
                                                        lineHeight: '1.2',
                                                      }}
                                                    >
                                                      <Markdown
                                                        source={feature.text}
                                                      />
                                                    </div>
                                                  )}
                                                {feature.subtitle && (
                                                  <div
                                                    style={{
                                                      fontSize: '12px',
                                                      fontWeight: '500',
                                                      color: '#000',
                                                      lineHeight: '1.3',
                                                    }}
                                                  >
                                                    {typeof feature.subtitle ===
                                                    'string' ? (
                                                      <Markdown
                                                        source={
                                                          feature.subtitle
                                                        }
                                                      />
                                                    ) : (
                                                      feature.subtitle
                                                    )}
                                                  </div>
                                                )}
                                                {feature && feature.code === tripflexCode.ENHANCED_TRIP_FLEX && feature.ancillaryType === ancillaryTypes.TRIP_FLEX && (
                                                  <RefundPill style = {{marginTop: '4px'}}>
                                                    <Markdown source={tripFlexOFOPLabelHTML} />
                                                  </RefundPill>
                                                )}
                                              </div>
                                              {feature.info && (
                                                <Popover
                                                  content={
                                                    <Box p={3}>
                                                      <Text
                                                        size={14}
                                                        weight={400}
                                                      >
                                                        <Markdown source={feature.ancillaryType === ancillaryTypes.TRIP_FLEX
                                                          ? (feature.code === tripflexCode.ENHANCED_TRIP_FLEX) ? tripFlexOFOPInfoDescriptionHTML : tripFlexInfoDescription
                                                          : feature.ancillaryType ===
                                                              'PRIORITY_BOARDING'
                                                            ? priorityBoardingInfoDescription
                                                            : ''} />
                                                      </Text>
                                                    </Box>
                                                  }
                                                  label={'info-popover'}
                                                  arrowSize={
                                                    mq.lte('sm') ? 8 : 10
                                                  }
                                                  backgroundColor={
                                                    theme.colors.popover
                                                      .background
                                                  }
                                                  maxWidth="300px"
                                                  showClose={mq.lte('sm')}
                                                  trigger={
                                                    mq.lte('md')
                                                      ? 'click'
                                                      : 'hover'
                                                  }
                                                  placement='right-center'
                                                >
                                                  <div
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                    }}
                                                    style={{
                                                      display: 'inline-flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      padding: '4px',
                                                      cursor: 'pointer',
                                                    }}
                                                  >
                                                    <InfoIcon
                                                      src={infoIconImage}
                                                      alt="More info"
                                                      style={{
                                                        width: mq.lte('md')
                                                          ? '20px'
                                                          : '16px',
                                                        height: mq.lte('md')
                                                          ? '20px'
                                                          : '16px',
                                                        cursor: 'pointer',
                                                        flexShrink: 0,
                                                      }}
                                                    />
                                                  </div>
                                                </Popover>
                                              )}
                                            </MobileBundleFeature>
                                          )
                                        )}
                                      </MobileBundleContent>
                                    )}
                                  </MobileBundleCard>
                                );
                              })}
                              <NoThanksButton
                                onClick={() => {
                                  setFlightSelected();
                                  setFlightBundleInfoOpen(null);
                                  handleNoBundle(); // Remove setNoThanksLinkClicked(true)
                                }}
                                data-hook={`flight-${flightOption.id}-noThanksButton`}
                              >
                                <Markdown source={skipBundleButtonLabelHTML} />
                              </NoThanksButton>
                            </div>
                          ) : (
                            <div>
                              <BundleOptions>
                                {bundleOptions.map((bundle) => {
                                  const isSelected =
                                    currentSelectedTier === bundle.tier &&
                                    validSelected;
                                  return (
                                    <BundleOption
                                      key={bundle.type}
                                      isSelected={isSelected}
                                      onClick={() => {
                                        if (isSelected) {
                                          handleNoBundle();
                                        } else {
                                          setFlightSelected();
                                          setFlightBundleInfoOpen(null);
                                          handleBundleSelection(bundle.tier);
                                        }
                                      }}
                                      data-hook={`flight-${flightOption.id}-bundleType-${bundle.type}-selected-${isSelected}`}
                                    >
                                      <BundleHeader type={bundle.type}>
                                        <TabBox
                                          color={get(theme.colors.bundle, [
                                            `tier_${bundle.tier}`,
                                            'label',
                                          ])}
                                        >
                                          <Box
                                            d="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                          >
                                            <BundleImage
                                              src={bundle.icon}
                                              alt="Bundle icon"
                                            />
                                          </Box>
                                        </TabBox>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                          <span
                                            style={{
                                              fontSize: '24px',
                                              fontWeight: '500',
                                            }}
                                          >
                                            +${bundle.price}*
                                          </span>
                                          <div style={{ fontSize: '12px', fontWeight: '400' }}>
                                            <Markdown source={bundlePageTextAssociatedWithCurrentPrice} />
                                          </div>
                                        </div>
                                      </BundleHeader>
                                      <BundleContent>
                                        {bundle.features.map(
                                          (feature, index) => (
                                            <BundleFeature
                                              key={index}
                                              style={{
                                                borderBottom:
                                                  index !==
                                                  bundle.features.length - 1
                                                    ? '1px solid #E5E7EB'
                                                    : 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                minHeight: '50px',
                                                padding: '0 0',
                                              }}
                                            >
                                              <AncillaryImage
                                                src={feature.icon}
                                                isBagIcon={feature.text === bagsIncludeLabelHTML}
                                              />
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  flexDirection: 'column',
                                                  flex: 1,
                                                }}
                                              >
                                                {feature.text &&
                                                  !feature.hasOnlySubtitle && (
                                                    <FeatureText
                                                      style={{
                                                        fontSize: '12px',
                                                        fontWeight: '400',
                                                        marginBottom:
                                                          feature.subtitle
                                                            ? '4px'
                                                            : '0',
                                                      }}
                                                    >
                                                      <Markdown
                                                        source={feature.text}
                                                      />
                                                    </FeatureText>
                                                  )}
                                                {feature.subtitle && (
                                                  <FeatureText
                                                    style={{
                                                      fontSize: '14px',
                                                      fontWeight: '500',
                                                    }}
                                                  >
                                                    {typeof feature.subtitle ===
                                                    'string' ? (
                                                      <Markdown
                                                        source={
                                                          feature.subtitle
                                                        }
                                                      />
                                                    ) : (
                                                      feature.subtitle
                                                    )}
                                                  </FeatureText>
                                                )}
                                                {feature && feature.code === tripflexCode.ENHANCED_TRIP_FLEX && feature.ancillaryType === ancillaryTypes.TRIP_FLEX && (
                                                  <RefundPill>
                                                    <Markdown source={tripFlexOFOPLabelHTML} />
                                                  </RefundPill>
                                                )}
                                              </div>
                                              {feature.info && (
                                                <Popover
                                                  content={
                                                    <Box p={3}>
                                                      <Text
                                                        size={14}
                                                        weight={400}
                                                      >
                                                        <Markdown source={feature.ancillaryType === ancillaryTypes.TRIP_FLEX
                                                          ? (feature.code === tripflexCode.ENHANCED_TRIP_FLEX) ? tripFlexOFOPInfoDescriptionHTML : tripFlexInfoDescription
                                                          : feature.ancillaryType ===
                                                              'PRIORITY_BOARDING'
                                                            ? priorityBoardingInfoDescription
                                                            : ''} />
                                                      </Text>
                                                    </Box>
                                                  }
                                                  label={'info-popover'}
                                                  arrowSize={10}
                                                  backgroundColor={
                                                    theme.colors.popover
                                                      .background
                                                  }
                                                  maxWidth="300px"
                                                  showClose={mq.lte('sm')}
                                                  placement='right-center'
                                                >
                                                  <InfoIcon
                                                    src={infoIconImage}
                                                    alt="More info"
                                                  />
                                                </Popover>
                                              )}
                                            </BundleFeature>
                                          )
                                        )}
                                      </BundleContent>
                                      <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'center' }}>
                                        {renderBundleAddButton(bundle, isSelected, 'addButton-desktop')}
                                      </div>
                                    </BundleOption>
                                  );
                                })}
                              </BundleOptions>
                            </div>
                          )
                        }
                      </Media>
                    </Box>
                  )}
                </Box>
              </FlightDetailsBox>
            </FlightBox>
          </FlightWrapper>
        )}
      </Media>
    );
  }
);
FlightItemWithBundles.propTypes = propTypes;

export default FlightItemWithBundles;